<template>
    <div class="subscription_content">
        <!-- No Subscription State -->
        <div v-if="!subscription" class="empty_state">
            <p class="info_message">
                ℹ️ This user does not have an active subscription
            </p>
        </div>

        <!-- Has Subscription -->
        <div v-else class="subscription_details">
            <!-- Status Badge -->
            <div class="detail_row">
                <span class="label">Status:</span>
                <span
                    class="status_badge"
                    :class="`status_${subscription.status}`"
                >
                    {{ formatStatus(subscription.status) }}
                </span>
            </div>

            <!-- Plan Name -->
            <div class="detail_row">
                <span class="label">Plan:</span>
                <span class="value bold">{{ subscription.plan.name }}</span>
            </div>

            <!-- Price -->
            <div class="detail_row">
                <span class="label">Price:</span>
                <span class="value">{{ formatPrice(subscription.plan) }}</span>
            </div>

            <!-- Downloads Limit -->
            <div class="detail_row">
                <span class="label">Downloads Limit:</span>
                <span class="value">{{
                    formatDownloadsLimit(subscription.plan)
                }}</span>
            </div>

            <!-- Current Period -->
            <div class="detail_section">
                <span class="subsection_title">Current Period:</span>
                <div class="detail_row indent">
                    <span class="label">Start:</span>
                    <span class="value">{{
                        formatDate(subscription.current_period_start)
                    }}</span>
                </div>
                <div class="detail_row indent">
                    <span class="label">End:</span>
                    <span class="value">{{
                        formatDate(subscription.current_period_end)
                    }}</span>
                </div>
            </div>

            <!-- Payment Provider -->
            <div v-if="subscription.payment_provider" class="detail_row">
                <span class="label">Payment Provider:</span>
                <span class="value">{{
                    capitalizeFirst(subscription.payment_provider)
                }}</span>
            </div>

            <!-- Auto-Renewal -->
            <div class="detail_row">
                <span class="label">Auto-Renewal:</span>
                <span
                    class="value"
                    :class="
                        subscription.cancel_at_period_end
                            ? 'text_danger'
                            : 'text_success'
                    "
                >
                    {{
                        subscription.cancel_at_period_end
                            ? '❌ Cancelled - expires on ' +
                              formatDate(subscription.current_period_end)
                            : '✓ Enabled'
                    }}
                </span>
            </div>

            <!-- Warning for Cancelled Subscriptions -->
            <div v-if="subscription.cancel_at_period_end" class="warning_box">
                ⚠️ This subscription will be cancelled on
                {{ formatDate(subscription.current_period_end) }}. The user will
                retain access until the end of the billing period.
            </div>

            <!-- Error for Past Due -->
            <div v-if="subscription.status === 'past_due'" class="error_box">
                ❌ Payment Failed - Subscription past due. Action required: User
                needs to update payment method.
            </div>
        </div>

        <!-- Reset Subscription Button (non-production only) -->
        <div v-if="isNonProduction" class="reset_subscription_section">
            <button
                class="btn_reset_subscription"
                :disabled="isResetting"
                @click="confirmResetSubscription"
            >
                {{ isResetting ? 'Resetting...' : 'Reset Subscription Data' }}
            </button>
            <span class="reset_hint"
                >⚠️ Dev/Test only - Deletes all subscription data</span
            >
        </div>
    </div>
</template>

<script>
import constants from '@/constants';

export default {
    props: {
        subscription: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            isResetting: false,
        };
    },
    computed: {
        isNonProduction() {
            return process.env.VUE_APP_VERSION === 'dev';
        },
        userId() {
            return this.$store.getters.USER_EDIT_DATA?.id;
        },
    },
    methods: {
        confirmResetSubscription() {
            this.$store.commit(
                'CONFIRMED_POPUP_MESSAGE',
                '⚠️ This will permanently delete ALL subscription data (payment transactions, subscription history, active/past subscriptions). The user will need to subscribe again.'
            );
            this.$store.commit(
                'CONFIRMED_POPUP_METHOD',
                this.executeResetSubscription.bind(this)
            );
            this.$store.commit(
                'SHOP_POPUP_NAME',
                constants.POPUPS_NAME.CONFIRMED
            );
        },

        async executeResetSubscription() {
            this.isResetting = true;

            try {
                const response = await this.callResetApi();
                if (!response) return;

                this.showSuccessPopup(response.deleted);
                this.refreshUserData();
            } finally {
                this.isResetting = false;
            }
        },

        async callResetApi() {
            const { resetUserSubscriptionData } = await import(
                '@/api/user.js'
            );
            return await resetUserSubscriptionData(this.userId);
        },

        showSuccessPopup(deleted) {
            this.$store.commit('POPUP_ADD', {
                title: 'SUBSCRIPTION DATA RESET',
                description: [
                    '✅ Successfully reset subscription data',
                    '',
                    `Deleted: ${deleted.payment_transactions} transactions, ${deleted.subscription_history} history records, ${deleted.user_subscriptions} subscriptions`,
                ],
                color: '#00d25b',
            });
        },

        refreshUserData() {
            this.$store.dispatch('loadUsers');
            this.$store.dispatch('loadUserDetails');
        },

        formatStatus(status) {
            const statusMap = {
                active: '🟢 Active',
                trialing: '🔵 Trial',
                past_due: '🔴 Payment Failed',
                cancelled: '⚫ Cancelled',
                unpaid: '🔴 Unpaid',
                incomplete: '🟡 Incomplete',
            };
            return statusMap[status] || status;
        },

        formatPrice(plan) {
            const amount = (plan.price_amount / 100).toFixed(2);
            return `$${amount} / ${plan.billing_period}`;
        },

        formatDownloadsLimit(plan) {
            if (
                !plan ||
                plan.max_downloads === null ||
                plan.max_downloads === undefined
            ) {
                return 'Unlimited downloads';
            }

            const period = plan.billing_period?.replace('ly', '') || 'month';
            return `${plan.max_downloads.toLocaleString(
                'en-US'
            )} downloads per ${period}`;
        },

        formatDate(isoString) {
            if (!isoString) return 'N/A';

            const date = new Date(isoString);

            // Format: Nov 1, 2025
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return date.toLocaleDateString('en-US', options);
        },

        capitalizeFirst(str) {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
    },
};
</script>

<style scoped>
.subscription_content {
    padding: 5px 0 0 0;
}

.empty_state {
    padding: 20px;
    text-align: center;
}

.info_message {
    color: var(--background-text, #ffffff);
    font-size: 15px;
    opacity: 0.8;
}

.subscription_details {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.detail_row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.detail_row.indent {
    padding-left: 20px;
}

.label {
    font-size: 14px;
    font-weight: 500;
    color: var(--background-text, #ffffff);
    opacity: 0.8;
    min-width: 150px;
}

.value {
    font-size: 16px;
    color: var(--background-text, #ffffff);
}

.value.bold {
    font-weight: 700;
    font-size: 18px;
}

.subsection_title {
    font-size: 15px;
    font-weight: 600;
    color: var(--background-text, #ffffff);
    display: block;
    margin-bottom: 8px;
}

.detail_section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.status_badge {
    display: inline-block;
    padding: 6px 14px;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 600;
}

.status_active {
    background-color: #d1fae5;
    color: #065f46;
}

.status_trialing {
    background-color: #dbeafe;
    color: #1e40af;
}

.status_past_due,
.status_unpaid {
    background-color: #fee2e2;
    color: #991b1b;
}

.status_cancelled {
    background-color: #f3f4f6;
    color: #4b5563;
}

.text_success {
    color: #10b981;
    font-weight: 500;
}

.text_danger {
    color: #ef4444;
    font-weight: 500;
}

.warning_box {
    background-color: #fef3c7;
    border-left: 4px solid #f59e0b;
    padding: 12px;
    border-radius: 4px;
    color: #92400e;
    font-size: 14px;
    margin-top: 10px;
}

.error_box {
    background-color: #fee2e2;
    border-left: 4px solid #ef4444;
    padding: 12px;
    border-radius: 4px;
    color: #991b1b;
    font-size: 14px;
    margin-top: 10px;
}

.reset_subscription_section {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid var(--light-98);
    display: flex;
    align-items: center;
    gap: 15px;
}

.btn_reset_subscription {
    padding: 8px 16px;
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
}

.btn_reset_subscription:hover:not(:disabled) {
    background-color: #c82333;
}

.btn_reset_subscription:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.reset_hint {
    font-size: 12px;
    color: #6c757d;
}
</style>
